/** @format */
import { useEffect, useMemo, useCallback } from 'react'
import { StyledBreadcrumb } from './breadcrumb-style'
import { Link } from 'react-router-dom'
import { useDatalibraryBreadcrumb } from '@/entities/breadcrumb/model'
import { useLocation } from 'react-router-dom'
import { useSafeNavigate } from '@/shared/lib/safe-navigate'
import { useTranslation } from 'react-i18next'
import { useThemeObject } from '@/entities/theme/model'

interface BreadcrumbItem {
  titleKey: string
  title: string
  path: string
  originalSegment: string
  isId: boolean
}

// Define known route segments (not IDs) — static, no need to recreate
const KNOWN_ROUTES = new Set([
  'data-library',
  'templates',
  'models',
  'users',
  'settings',
  'create-template',
  'edit-template',
  'view-template',
  'create',
  'edit',
  'view',
  'delete',
  'copy',
  'duplicate',
  'export',
  'import',
  'upload',
  'download',
  'manage',
  'admin',
  'profile',
  'dashboard',
  'reports',
  'analytics',
  'logs',
  'help',
  'docs',
  'api',
  'webhooks',
  'integrations',
  'notifications',
  'permissions',
  'roles',
  'teams',
  'projects',
  'workflows',
  'categories',
  'tags',
  'search',
  'filter',
  'sort',
  'config',
  'setup',
  'sales',
  'orders',
  'all',
  'new',
  'in-process',
  'completed',
  'canceled',
])

// Pre-compiled regex patterns — static, no need to recreate per render
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const OBJECT_ID_PATTERN = /^[0-9a-f]{24}$/i
const NUMBER_PATTERN = /^\d+$/
const ALPHANUMERIC_PATTERN = /^[a-zA-Z]+\d+$|^\d+[a-zA-Z]+$/
const MIXED_PATTERN = /^[a-zA-Z0-9]{6,}$/
const PREFIX_PATTERN = /^(id|ref|key|token|hash|code)[-_]?[a-zA-Z0-9]+$/i
// Domain entity-id format: 1-5 uppercase letters + dash + digits, optionally
// followed by more `-digits` groups. Catches accounting (PAY-2605-003 /
// RCT-2605-001 / T-2605-001), supply refs (PRM-2605-0007), and any similar
// `PREFIX-YEAR-SEQ` schemes. Lowercase route segments (`data-library`,
// `create-template`, etc.) are not matched because the leading char class
// is `[A-Z]`.
const ENTITY_CODE_PATTERN = /^[A-Z]{1,5}-\d+(-\d+)*$/

function isLikelyId(segment: string): boolean {
  if (!segment) return false
  if (KNOWN_ROUTES.has(segment)) return false
  if (UUID_PATTERN.test(segment)) return true
  if (OBJECT_ID_PATTERN.test(segment)) return true
  if (NUMBER_PATTERN.test(segment)) return true
  if (ALPHANUMERIC_PATTERN.test(segment)) return true
  if (MIXED_PATTERN.test(segment) && /\d/.test(segment) && /[a-zA-Z]/.test(segment)) {
    return true
  }
  if (PREFIX_PATTERN.test(segment)) return true
  if (ENTITY_CODE_PATTERN.test(segment)) return true
  return false
}

const Breadcrumb = () => {
  const { t, i18n } = useTranslation()
  const theme = useThemeObject()
  const navigate = useSafeNavigate()
  const { setBreadcrumbList } = useDatalibraryBreadcrumb()
  const { pathname } = useLocation()
  const isLoginPage = pathname === '/login'

  const SPECIAL_ITEMS = useMemo(() => [t('sections'), t('rows'), t('columns'), t('items')], [t])

  // Derive breadcrumbs from pathname + translation — single useMemo replaces 3 useEffects
  const breadcrumbs = useMemo(() => {
    const segments = pathname
      .split('/')
      .filter((item) => item)
      .map((item, index, arr) => ({
        titleKey: item,
        title: i18n.exists(item) ? t(item) : item,
        path: `/${arr.slice(0, index + 1).join('/')}`,
        originalSegment: item,
        isId: isLikelyId(item),
      }))

    return [
      {
        titleKey: '/',
        title: i18n.exists('/') ? t('/') : '/',
        path: '/',
        originalSegment: '',
        isId: false,
      },
      ...segments,
    ]
  }, [pathname, t, i18n])

  // Sync breadcrumbs to the store
  useEffect(() => {
    setBreadcrumbList(breadcrumbs)
  }, [breadcrumbs, setBreadcrumbList])

  const handleBreadcrumbClick = useCallback(
    (item: BreadcrumbItem) => {
      if (item.isId) return
      if (item.path === pathname) return
      navigate(item.path, { replace: true })
    },
    [navigate, pathname],
  )

  const itemRender = useCallback(
    (currentRoute: BreadcrumbItem, _params: Record<string, string>, items: BreadcrumbItem[]) => {
      const isLast = currentRoute?.path === items[items.length - 1]?.path
      const index = items.findIndex((item: BreadcrumbItem) => item.path === currentRoute.path)
      const previousItem = items[index - 1]

      const isSpecial = SPECIAL_ITEMS.includes(currentRoute.title)
      const isId = currentRoute.isId

      const textColor = isLoginPage ? theme.loginPgaeHeaderText : theme.navigationTextColor

      if (isSpecial) {
        return (
          <span
            style={{
              color: textColor,
              cursor: previousItem ? 'pointer' : 'default',
              textDecoration: previousItem ? 'underline' : 'none',
            }}
            onClick={() => {
              if (previousItem) {
                handleBreadcrumbClick(previousItem)
              }
            }}
          >
            {currentRoute.title}
          </span>
        )
      }

      if (isId) {
        return (
          <span
            className='id-segment'
            style={{
              color: '#999',
              cursor: 'default',
              fontFamily: 'JetBrains Mono, monospace',
              background: '#f5f5f5',
              padding: '2px 6px',
              borderRadius: '3px',
              fontSize: '12px',
              fontWeight: '500',
            }}
          >
            {currentRoute.originalSegment}
          </span>
        )
      }

      if (isLast) {
        return <span style={{ color: textColor }}>{currentRoute.title}</span>
      }
      return (
        <Link
          style={{ color: textColor }}
          to={currentRoute.path}
          onClick={(e) => {
            e.preventDefault()
            handleBreadcrumbClick(currentRoute)
          }}
        >
          {currentRoute.title}
        </Link>
      )
    },
    [SPECIAL_ITEMS, isLoginPage, theme, handleBreadcrumbClick],
  )

  return (
    <div>
      <StyledBreadcrumb
        itemRender={itemRender}
        items={breadcrumbs}
        theme={theme}
        $isLoginPage={isLoginPage}
        separator={
          <span
            style={{
              color: isLoginPage ? theme.loginPgaeHeaderText : theme.navigationTextColor,
            }}
          >
            |
          </span>
        }
      />
    </div>
  )
}

export default Breadcrumb
